import type {
  DisplayFilters,
  DisplayGroups,
  EditorElement,
} from '@wix/component-protocol/schema';

import { ManifestDisplayFilterResolver } from './ManifestDisplayFilterResolver';
import { ManifestDefinitionsResolver } from './ManifestDefinitionsResolver';
import { CacheMethodResponse } from '../decorators';
import { MethodCacheStrategy } from '../MethodCacheStrategy';
import { ManifestServiceFlags } from '../../ManifestServiceFlags';
import {
  VirtualElement,
  VirtualElementAPIType,
} from '../providers/VirtualElement';
import { ManifestServiceStats } from '../../ManifestServiceStats';
import { AbstractManifestAPI } from '../AbstractManifestAPI';
import { IManifestChangeDetection } from '../../ManifestChangeDetection';
import { ManifestDefaultValuesResolver } from './ManifestDefaultValuesResolver';
import { ManifestResolverAPI } from '../../handlers';
import type { EditorPointerServiceAPI } from '@wix/editor-document-logic-api';

export class ManifestResolver extends AbstractManifestAPI<
  ManifestResolverAPI,
  VirtualElementAPIType.ManifestResolver
> {
  private readonly displayFilterResolver: ManifestDisplayFilterResolver;
  private readonly definitionsResolver: ManifestDefinitionsResolver;
  private readonly manifestDefaultValuesResolver: ManifestDefaultValuesResolver;

  constructor(
    node: VirtualElement<VirtualElementAPIType.ManifestResolver>,
    api: ManifestResolverAPI,
    editorPointerAPI: EditorPointerServiceAPI,
    manifestChangeDetection: IManifestChangeDetection,
    manifestServiceFlags: ManifestServiceFlags,
    manifestServiceStats: ManifestServiceStats,
  ) {
    super(
      node,
      api,
      editorPointerAPI,
      manifestChangeDetection,
      manifestServiceFlags,
      manifestServiceStats,
    );

    this.displayFilterResolver = new ManifestDisplayFilterResolver(
      this.node,
      this.api,
      this.editorPointerAPI,
      this.manifestChangeDetection,
      this.manifestServiceFlags,
      this.manifestServiceStats,
    );

    this.definitionsResolver = new ManifestDefinitionsResolver(
      this.node,
      this.api,
      this.editorPointerAPI,
      this.manifestChangeDetection,
      this.manifestServiceFlags,
      this.manifestServiceStats,
    );

    this.manifestDefaultValuesResolver = new ManifestDefaultValuesResolver(
      this.node,
      this.api,
      this.editorPointerAPI,
      this.manifestChangeDetection,
      this.manifestServiceFlags,
      this.manifestServiceStats,
    );
  }

  @CacheMethodResponse({
    namespace: 'ManifestResolver',
    cacheStrategy: MethodCacheStrategy.Permanent,
  })
  getDisplayName() {
    const editorPointer = this.virtualManifest.tree.editorPointer;

    // resolve name for child
    if (editorPointer) {
      const innerElementPath = editorPointer.innerElementPath ?? [];
      const fallbackDisplayName =
        innerElementPath[innerElementPath.length - 1] ?? 'Element';

      return (
        this.virtualManifest.tree.manifest.editorElement?.displayName ??
        fallbackDisplayName
      );
    }

    const manifests = this.virtualManifest.manifests.filter(
      (x) => !x.isAutoInjected,
    );

    return [
      ...new Set(
        manifests.map(
          (x) => x.store.manifest.editorElement?.displayName ?? 'Component',
        ),
      ),
    ].join(', ');
  }

  getEditableItems() {
    return this.displayFilterResolver.getEditableItems();
  }

  @CacheMethodResponse({
    namespace: 'ManifestResolver',
    cacheStrategy: MethodCacheStrategy.PresetOrStateUpdate,
  })
  isElementEditable() {
    /**
     * probably we need to check every parent...
     *
     * this.node.getParents().every(x => x.getAPI().isElementEditable())
     */
    if (this.node.isMultipleComponentsRoot() || this.node.isRoot()) {
      return true;
    }

    const parent = this.node.getParent();
    if (!parent || !this.virtualManifest.tree.elementName) {
      return false;
    }

    const editableItems = parent.getAPI().getEditableItems();
    return editableItems.elements.has(this.virtualManifest.tree.elementName);
  }

  @CacheMethodResponse({
    namespace: 'ManifestResolver',
    cacheStrategy: MethodCacheStrategy.PresetOrStateUpdate,
  })
  getStyleDefinitions() {
    return this.definitionsResolver.getStyleDefinitions();
  }

  @CacheMethodResponse({
    namespace: 'ManifestResolver',
    cacheStrategy: MethodCacheStrategy.PresetOrStateUpdate,
  })
  getDataDefinitions(): NonNullable<EditorElement['data']> {
    return this.definitionsResolver.getDataDefinitions();
  }

  @CacheMethodResponse({
    namespace: 'ManifestResolver',
    cacheStrategy: MethodCacheStrategy.PresetOrStateUpdate,
  })
  getCustomActionDefinitions(): NonNullable<EditorElement['customActions']> {
    return this.definitionsResolver.getCustomActionDefinitions();
  }

  @CacheMethodResponse({
    cacheStrategy: MethodCacheStrategy.PresetOrStateUpdate,
  })
  getActionDefinitions(): NonNullable<EditorElement['actions']> {
    return this.definitionsResolver.getActionDefinitions();
  }

  @CacheMethodResponse({
    namespace: 'ManifestResolver',
    cacheStrategy: MethodCacheStrategy.PresetOrStateUpdate,
  })
  getStateDefinitions(): NonNullable<EditorElement['states']> {
    return this.definitionsResolver.getStateDefinitions();
  }

  @CacheMethodResponse({
    namespace: 'ManifestResolver',
    cacheStrategy: MethodCacheStrategy.PresetOrStateUpdate,
  })
  getDesignStateDefinitions(): NonNullable<EditorElement['states']> {
    return this.definitionsResolver.getDesignStateDefinitions();
  }

  @CacheMethodResponse({
    namespace: 'ManifestResolver',
    cacheStrategy: MethodCacheStrategy.PresetOrStateUpdate,
  })
  getPresetDefinitions(): NonNullable<EditorElement['presets']> {
    return this.definitionsResolver.getPresetDefinitions();
  }

  @CacheMethodResponse({
    namespace: 'ManifestResolver',
    cacheStrategy: MethodCacheStrategy.PresetOrStateUpdate,
  })
  getLayoutDefinitions(): NonNullable<EditorElement['layout']> {
    return this.definitionsResolver.getLayoutDefinitions();
  }

  @CacheMethodResponse({
    namespace: 'ManifestResolver',
    cacheStrategy: MethodCacheStrategy.PresetOrStateUpdate,
  })
  getDisplayGroupDefinitions(options?: {
    filter?: { groupType?: DisplayGroups[string]['groupType'] };
  }): NonNullable<EditorElement['displayGroups']> {
    return this.definitionsResolver.getDisplayGroupDefinitions(options);
  }

  @CacheMethodResponse({
    namespace: 'ManifestResolver',
    cacheStrategy: MethodCacheStrategy.PresetOrStateUpdate,
  })
  getDisplayFilters<K extends keyof DisplayFilters>(
    filterType: K,
  ): DisplayFilters[K] | undefined {
    return this.displayFilterResolver.getDisplayFilters(filterType);
  }

  getDefaultDataValue(dataKey: string) {
    return this.manifestDefaultValuesResolver.getDefaultDataValue(dataKey);
  }

  getDefaultCssPropertyValue(styleKey: string) {
    return this.manifestDefaultValuesResolver.getDefaultCssPropertyValue(
      styleKey,
    );
  }

  getDefaultCustomCssPropertyValue(styleKey: string) {
    return this.manifestDefaultValuesResolver.getDefaultCustomCssPropertyValue(
      styleKey,
    );
  }

  getDefaultStyleValue(styleKey: string) {
    return this.manifestDefaultValuesResolver.getDefaultStyleValue(styleKey);
  }

  getDefaultStyleValues() {
    return this.manifestDefaultValuesResolver.getDefaultStyleValues();
  }

  getDefaultDataValues() {
    return this.manifestDefaultValuesResolver.getDefaultDataValues();
  }
}
